<template>
  <div id="launcher">
    <template v-if="loadingProcess">
      <span id="message" v-if="message">{{ message }}</span>
      <Loader type="line" color="#c7c7c7"/>
    </template>
    <template v-else>
      <div class="screen">
        <Media
          type="background_image"
          :media="entranceConfig.screenbgName"
          :entranceConfig="entranceConfig"/>
        <keep-alive>
          <router-view
            :entranceConfig="entranceConfig"
            :contents="contents_data"
            :clock="clock"
            :connectionError="connectionError"/>
        </keep-alive>
      </div>
    </template>
  </div>
</template>

<style lang="sass" scoped>
#launcher
  align-items: center
  display: flex
  justify-content: center
#message
  bottom: 1rem
  color: #bdbdbd
  font-size: .8em
  position: absolute
</style>

<script>
import AppIcon from '@/components/icons/AppIcon.vue';
import Loader from '@/components/appModules/Loader.vue';
import Media from '@/components/appModules/Media.vue';

export default {
  name: 'Launcher',

  components: {
    AppIcon,
    Loader,
    Media,
  },

  data() {
    return {
      appWorker: null,
      clock: null,
      connectionError: null,
      contents_data: null,
      contents_recoveryData: null,
      entranceConfig: null,
      loadingProcess: true,
      message: null,
    };
  },

  created() {
    this.appWorker = new Worker('@/worker.js', { type: 'module' });

    this.appWorker.onmessage = e => {
      const deviceID = JSON.parse(localStorage.getItem('initConfig')).deviceID;
      const serverUrl = JSON.parse(localStorage.getItem('initConfig')).serverUrl;
      const registeredVersion = JSON.parse(localStorage.getItem('registrationData')).registeredVersion;

      // console.log(e.data);
      switch (e.data.action) {

        // --- Mise à jour de l'horloge :
        case 'processAppTimeSetting':
          this.clock = e.data.result;
          break;
        
        // --- Gestion des erreurs :
        case 'processAppError':
          console.log(e.data);
          this.connectionError = e.data.error.status === 'NO_INTERNET'
            ? 'internet'
            : 'server';
          
          this.message = this.connectionError === 'internet'
            ? 'En attente de connexion internet'
            : 'En attente des serveurs Oecko';

          if (this.$route.name === 'launcher' && this.contents_data && this.entranceConfig.gridModulesList.length) {
            this.message = null;
            this.loadingProcess = false;
            this.$router.push({ name: 'home' });
          }
          
          if (e.data.error.dataManagement) {
            if (e.data.error.dataManagement.recoveryData) {
              this.contents_recoveryData[e.data.error.dataManagement.type] = e.data.error.dataManagement.recoveryData;
              localStorage.setItem('contents_recoveryData', JSON.stringify(this.contents_recoveryData));
            }
          }

          this.appManagement(50000);
          break;

        // --- Rien de particulier :
        case 'processInfo':
          if (this.$route.name === 'launcher') {
            this.message = null;
            this.loadingProcess = false;
            this.$router.push({ name: 'home' });
          }
          if (this.connectionError) this.connectionError = null;
          this.appManagement(25000);
          break;
        
        // --- Gestion des données :
        case 'processDataManagement':
          console.log(e.data.dataManagement);
          if (this.$route.name === 'launcher') {
            this.message = null;
            this.loadingProcess = false;
            this.$router.push({ name: 'home' });
          }

          if (e.data.dataManagement.recoveryData) {
            this.contents_recoveryData[e.data.dataManagement.type] = e.data.dataManagement.recoveryData;
            localStorage.setItem('contents_recoveryData', JSON.stringify(this.contents_recoveryData));
          }

          if (e.data.dataManagement.contents) {
            this.$set(this.contents_data, e.data.dataManagement.type, e.data.dataManagement.contents)
            localStorage.setItem('contents_data', JSON.stringify(this.contents_data));
          }

          if (e.data.dataManagement.entranceConfig) {
            this.entranceConfig = e.data.dataManagement.entranceConfig;
            localStorage.setItem('entranceConfig', JSON.stringify(this.entranceConfig));
          }

          if (this.connectionError) this.connectionError = null;
          this.appManagement(25000);
          break;
        
        // --- Changement de statut :
        case 'processStatusChange':
          const navConfig = {};

          if (e.data.status === 'NOT_AFFECTED') {
            localStorage.removeItem('entranceConfig');
            if (localStorage.getItem('contents_data')) localStorage.removeItem('contents_data');
            if (localStorage.getItem('contents_recoveryData')) localStorage.removeItem('contents_recoveryData');
            navConfig.operation = 'affectation';
            navConfig.data = { deviceID, serverUrl, registeredVersion };
          }

          if (e.data.status === 'NOT_REGISTERED') {
            localStorage.removeItem('entranceConfig');
            if (localStorage.getItem('contents_data')) localStorage.removeItem('contents_data');
            if (localStorage.getItem('contents_recoveryData')) localStorage.removeItem('contents_recoveryData');
            localStorage.removeItem('registrationData');
            navConfig.operation = 'registration';
            navConfig.data = { deviceID, serverUrl };
          }

          this.$router.push({
            name: 'init',
            params: {
              operation: navConfig.operation,
              data: navConfig.data,
            }
          });
          break

        case 'shutdown':
          this.appWorker.terminate();
          this.appWorker = null;

          this.$router.push({
            name: 'launcher',
          });
          this.loadingProcess = true;
          this.message = 'Mise en veille de votre écran : à demain !'
          break;

        default:
          break;
      }
    }
  },

  mounted() {
    if (
      !localStorage.getItem('entranceConfig')
      || !localStorage.getItem('contents_data')
      || !localStorage.getItem('contents_recoveryData')
    ) {
      this.restart();
    } else {
      this.entranceConfig = JSON.parse(localStorage.getItem('entranceConfig'));
      this.contents_data = JSON.parse(localStorage.getItem('contents_data'));
      this.contents_recoveryData = JSON.parse(localStorage.getItem('contents_recoveryData'));
      this.appManagement();
    }
  },

  beforeDestroy() {
    console.log('Quitte launcher');
    this.appWorker.terminate();
    this.appWorker = null;
    this.contents = null;
    this.entranceConfig = null;
    this.clock = null;
    this.loadingProcess = true;
    this.message = null;
  },

  methods: {
    appManagement(delay = 0) {
      const deviceID = JSON.parse(localStorage.getItem('initConfig')).deviceID;
      const serverUrl = JSON.parse(localStorage.getItem('initConfig')).serverUrl;

      this.appWorker.postMessage({
        action: 'appManagement',
        params: {
          delay,
          deviceID,
          config: {
            city: this.entranceConfig.city,
            coords: this.entranceConfig.weatherLocation,
            entrance: this.entranceConfig.entranceId,
          },
          contents_recoveryData: this.contents_recoveryData,
          clock: this.clock,
          modulesList: this.entranceConfig.gridModulesList,
          serverUrl,
        },
      });
    },

    restart() {
      localStorage.removeItem('entranceConfig');
      if (localStorage.getItem('contents_data')) localStorage.removeItem('contents_data');
      if (localStorage.getItem('contents_recoveryData')) localStorage.removeItem('contents_recoveryData');
      localStorage.removeItem('registrationData');
      navConfig.operation = 'registration';
      navConfig.data = { deviceID, serverUrl };

      this.$router.push({
        name: 'init',
        params: {
          operation: navConfig.operation,
          data: navConfig.data,
        }
      });
    },
  },

  
};
</script>